import * as React from 'react';
import { AlertTriangle, Search } from 'lucide-react';

import { Input } from '../primitives/input';
import { cn } from '../lib/utils';

/**
 * Form-field primitives that complement the existing `FormShell`
 * layout to satisfy the create/edit form contract:
 *
 *   1. Domain pickers (`DomainPickerField`) — search-driven; never raw GUID
 *   2. Per-field validation (callers wire react-hook-form / zod; FormShell
 *      already renders the validation summary)
 *   3. Helper text (existing primitive `<FormField description>`)
 *   4. Server-side error mapping (`ServerErrorAnchor` — anchored under field)
 *   5. Duplicate detection (`DuplicateBanner` — fed by `useDuplicateCheck`)
 *   6. Optimistic rollback (`useOptimisticForm` — restores snapshot on submit error)
 *   7. Submit + cancel + dirty-guard (existing FormShell footer)
 *
 * Coverage tracker: `scripts/audit-form-fields-coverage.mjs`.
 */

export interface DomainPickerOption {
  id: string;
  label: string;
  description?: string;
}

export interface DomainPickerFieldProps {
  id: string;
  /** Currently selected entity id, or null. */
  value: string | null;
  /** Optional pre-resolved label for the current value (avoids round-trip). */
  valueLabel?: string;
  onChange: (id: string | null, opt: DomainPickerOption | null) => void;
  onSearch: (query: string) => Promise<DomainPickerOption[]>;
  placeholder?: string;
  disabled?: boolean;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  /** Test id suffix; defaults to `id`. */
  testId?: string;
}

/**
 * Search-driven domain picker. Replaces free-form GUID/free-text entry for
 * supplier / material / equipment / document / part references etc.
 */
export function DomainPickerField({
  id,
  value,
  valueLabel,
  onChange,
  onSearch,
  placeholder = 'Search…',
  disabled,
  ariaInvalid,
  ariaDescribedBy,
  testId,
}: DomainPickerFieldProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState<DomainPickerOption[]>([]);
  const [resolved, setResolved] = React.useState<string | null>(valueLabel ?? null);
  const [loading, setLoading] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const listboxId = `${id}-listbox`;
  const optionId = React.useCallback(
    (optId: string) => `${id}-option-${optId}`,
    [id]
  );
  const listRef = React.useRef<HTMLUListElement | null>(null);
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  // The search input is the primary control of the popup; focus it
  // programmatically on open instead of using the autoFocus prop.
  React.useEffect(() => {
    if (open) searchInputRef.current?.focus();
  }, [open]);

  React.useEffect(() => {
    if (valueLabel != null) setResolved(valueLabel);
  }, [valueLabel]);

  React.useEffect(() => {
    if (!open) return;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        setResults(await onSearch(query));
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, open, onSearch]);

  // Reset / clamp the active option whenever the result set changes.
  React.useEffect(() => {
    setActiveIndex((i) => (results.length === 0 ? 0 : Math.min(i, results.length - 1)));
  }, [results]);

  // Keep the active option scrolled into view as the user arrows through.
  React.useEffect(() => {
    if (!open || results.length === 0) return;
    const opt = results[activeIndex];
    if (!opt) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `#${CSS.escape(optionId(opt.id))}`
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open, results, optionId]);

  const selectOption = (opt: DomainPickerOption) => {
    setResolved(opt.label);
    onChange(opt.id, opt);
    setOpen(false);
    setQuery('');
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (results.length > 0) setActiveIndex((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (results.length > 0) setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = results[activeIndex];
      if (opt) selectOption(opt);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const activeOption = results[activeIndex];

  return (
    <div className="relative">
      <button
        type="button"
        id={id}
        className={cn(
          'flex w-full items-center justify-between rounded border border-border bg-background px-3 py-2 text-left text-sm',
          ariaInvalid && 'border-[hsl(var(--alarm-critical))]/50',
          disabled && 'opacity-50'
        )}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        data-testid={`domain-picker-${testId ?? id}`}
      >
        <span className={cn(!resolved && !value && 'text-muted-foreground')}>
          {resolved ?? value ?? placeholder}
        </span>
        <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
      </button>
      {open ? (
        <div className="absolute z-20 mt-1 w-full rounded-md border bg-background shadow-lg">
          <Input
            ref={searchInputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={placeholder}
            className="m-2"
            role="combobox"
            aria-expanded
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={activeOption ? optionId(activeOption.id) : undefined}
            data-testid={`domain-picker-input-${testId ?? id}`}
          />
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            className="max-h-56 overflow-auto p-1"
          >
            {loading ? (
              <li className="px-3 py-2 text-sm text-muted-foreground">Searching…</li>
            ) : results.length === 0 ? (
              <li className="px-3 py-2 text-sm text-muted-foreground">No matches</li>
            ) : (
              results.map((opt, i) => (
                <li
                  key={opt.id}
                  id={optionId(opt.id)}
                  role="option"
                  aria-selected={value === opt.id}
                  className={cn(
                    'cursor-pointer rounded px-3 py-2 text-sm hover:bg-accent',
                    i === activeIndex && 'bg-accent'
                  )}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => selectOption(opt)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      selectOption(opt);
                    }
                  }}
                  data-testid={`domain-picker-option-${testId ?? id}-${opt.id}`}
                >
                  <div className="font-medium">{opt.label}</div>
                  {opt.description ? (
                    <div className="text-xs text-muted-foreground">{opt.description}</div>
                  ) : null}
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export interface DuplicateMatch {
  id: string;
  label: string;
  href?: string;
}

/** Banner that lists potential duplicates while the user is creating. */
export function DuplicateBanner({ matches }: { matches: DuplicateMatch[] }) {
  if (matches.length === 0) return null;
  return (
    <div
      className="flex items-start gap-2 rounded border border-[hsl(var(--alarm-minor))]/40 bg-[hsl(var(--alarm-minor))]/10 p-3 text-sm text-[hsl(var(--alarm-minor))]"
      role="alert"
      data-testid="duplicate-banner"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4" aria-hidden />
      <div>
        <div className="font-medium">Possible duplicates</div>
        <ul className="mt-1 list-disc pl-4">
          {matches.map((m) => (
            <li key={m.id}>
              {m.href ? (
                <a href={m.href} className="underline">
                  {m.label}
                </a>
              ) : (
                m.label
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Anchored server-error message (rendered below the input). */
export function ServerErrorAnchor({
  fieldId,
  message,
}: {
  fieldId: string;
  message?: string | null;
}) {
  if (!message) return null;
  return (
    <p
      id={`${fieldId}-server-error`}
      className="mt-1 text-xs text-[hsl(var(--alarm-critical))]"
      role="alert"
      data-testid={`server-error-${fieldId}`}
    >
      {message}
    </p>
  );
}

/**
 * Hook implementing the optimistic-rollback contract:
 *
 *   const form = useOptimisticForm({ initialValues, onSubmit });
 *   <input value={form.values.name} onChange={(e) => form.setField('name', e.target.value)} />
 *   <Button onClick={form.submit} disabled={form.submitting}>Save</Button>
 *
 * On submit error, `values` is rolled back to the pre-submit snapshot, the
 * caller can read `form.error`, and dirty state is preserved so the operator
 * can fix and retry.
 */
export interface UseOptimisticFormOpts<T> {
  initialValues: T;
  onSubmit: (values: T) => Promise<void>;
}

export interface UseOptimisticFormResult<T> {
  values: T;
  setField: <K extends keyof T>(key: K, value: T[K]) => void;
  setValues: React.Dispatch<React.SetStateAction<T>>;
  submit: () => Promise<void>;
  submitting: boolean;
  error: string | null;
  dirty: boolean;
  reset: () => void;
}

export function useOptimisticForm<T>(opts: UseOptimisticFormOpts<T>): UseOptimisticFormResult<T> {
  const [values, setValues] = React.useState<T>(opts.initialValues);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [dirty, setDirty] = React.useState(false);
  const initialRef = React.useRef<T>(opts.initialValues);

  const setField = React.useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setValues((v) => ({ ...v, [key]: value }));
    setDirty(true);
  }, []);

  const submit = React.useCallback(async () => {
    if (submitting) return;
    setError(null);
    const snapshot = values;
    setSubmitting(true);
    try {
      await opts.onSubmit(values);
      initialRef.current = snapshot;
      setDirty(false);
    } catch (e) {
      // Optimistic rollback — restore snapshot.
      setValues(snapshot);
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }, [values, submitting, opts]);

  const reset = React.useCallback(() => {
    setValues(initialRef.current);
    setError(null);
    setDirty(false);
  }, []);

  return { values, setField, setValues, submit, submitting, error, dirty, reset };
}

/**
 * Debounced duplicate-check hook. Pass a stable `predicate` function and the
 * current values; returns the latest match list.
 */
export function useDuplicateCheck<T>(
  values: T,
  predicate: (values: T) => Promise<DuplicateMatch[]> | DuplicateMatch[],
  delayMs = 400
): DuplicateMatch[] {
  const [matches, setMatches] = React.useState<DuplicateMatch[]>([]);
  React.useEffect(() => {
    const id = setTimeout(async () => {
      try {
        setMatches(await Promise.resolve(predicate(values)));
      } catch {
        // Non-blocking
      }
    }, delayMs);
    return () => clearTimeout(id);
  }, [values, predicate, delayMs]);
  return matches;
}
